import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Calendar, Clock, AlertTriangle, Plus, Trash2, CheckCircle } from 'lucide-react';
import api from '../../lib/api';

const AbsenceReportTab = ({ user }) => {
  const [absences, setAbsences] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [form, setForm] = useState({
    child_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [absencesRes, childrenRes] = await Promise.all([
        api.get('/absences'),
        api.get('/auth/my-children')
      ]);
      setAbsences(absencesRes.data || []);
      setChildren(childrenRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.child_id || !form.start_date) {
      toast.error('Please select a student and start date');
      return;
    }

    setSubmitting(true);
    try {
      const child = children.find(c => c.id === form.child_id || c.student_id === form.child_id);
      const studentName = child?.name || child?.student_name || 
        (child?.first_name ? `${child.first_name} ${child.last_name}` : form.child_id);
      
      await api.post('/absences/report', {
        student_name: studentName,
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        reason: form.reason || 'Not specified',
        notes: ''
      });
      
      toast.success('Absence reported successfully');
      setForm({ child_id: '', start_date: '', end_date: '', reason: '' });
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report absence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (absenceId) => {
    if (!window.confirm('Are you sure you want to cancel this absence report?')) {
      return;
    }
    
    try {
      await api.delete(`/absences/${absenceId}`);
      toast.success('Absence report cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel absence report');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-900">Absence Reports</h2>
          <p className="text-neutral-600 text-sm">
            Let us know when your student will be absent from class
          </p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
          data-testid="report-absence-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Report Absence
        </Button>
      </div>

      {/* Report Form */}
      {showForm && (
        <Card className="border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              Report an Absence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Student</Label>
                  <select
                    value={form.child_id}
                    onChange={(e) => setForm({ ...form, child_id: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-neutral-300 bg-white focus:ring-2 focus:ring-purple-500"
                    required
                    data-testid="absence-student-select"
                  >
                    <option value="">Select a student</option>
                    {children.map(child => (
                      <option key={child.id || child.student_id} value={child.id || child.student_id}>
                        {child.name || child.student_name || `${child.first_name} ${child.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Reason (optional)</Label>
                  <Input
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="e.g., Holiday, Illness"
                    data-testid="absence-reason"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    min={today}
                    required
                    data-testid="absence-start-date"
                  />
                </div>
                
                <div>
                  <Label>End Date (leave blank for single day)</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    min={form.start_date || today}
                    data-testid="absence-end-date"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-purple-500 hover:bg-purple-600"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Submit Report
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowForm(false); setForm({ child_id: '', start_date: '', end_date: '', reason: '' }); }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Existing Reports */}
      {absences.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500">No absence reports</p>
            <p className="text-neutral-400 text-sm">
              When your child needs to miss class, report it here so we know not to expect them.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {absences.map((absence) => {
            const isPast = new Date(absence.end_date) < new Date(today);
            const isActive = new Date(absence.start_date) <= new Date() && new Date(absence.end_date) >= new Date();
            
            return (
              <Card 
                key={absence.id} 
                className={`${
                  isPast ? 'bg-neutral-50 border-neutral-200' :
                  isActive ? 'bg-orange-50 border-orange-200' :
                  'bg-white border-purple-200'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-neutral-900">{absence.student_name}</h3>
                        {isActive ? (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Currently Absent
                          </Badge>
                        ) : isPast ? (
                          <Badge className="bg-neutral-100 text-neutral-600">
                            Past
                          </Badge>
                        ) : (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Upcoming
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-neutral-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(absence.start_date)}
                          {absence.end_date !== absence.start_date && (
                            <> - {formatDate(absence.end_date)}</>
                          )}
                        </span>
                        {absence.reason && (
                          <span className="text-neutral-500">
                            Reason: {absence.reason}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!isPast && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancel(absence.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`cancel-absence-${absence.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AbsenceReportTab;
